/**
 * @module CameraExtended
 */

const CenterOn = ( rect, x, y ) => {
    rect.x = x - rect.width / 2;
    rect.y = y - rect.height / 2;
    return rect;
};
const Linear = ( a, b, k ) => ( b - a ) * k + a;

/**
 * Camera with additional capabilities:
 * - Pan: automated movement to a location
 * - Offset: additional translation applied after scroll
 * - Scale Axes: ability to scale axes independently
 * @class CameraExtended
 * @extends Phaser.Cameras.Scene2D.Camera
 * @param {Number} x - horizontal position of viewport
 * @param {Number} y - vertical position of viewport
 * @param {Number} width - horizontal dimension of viewport
 * @param {Number} height - vertical dimension of viewport
 */

const CameraExtended = new Phaser.Class( {
    Extends: Phaser.Cameras.Scene2D.Camera,

    initialize: function CameraExtended( x, y, width, height ) {
        Phaser.Cameras.Scene2D.Camera.call( this, x, y, width, height );

        /**
         * Offset value: shifts the viewport after other transforms
         * @type {Phaser.Math.Vector2}
         * @private
         */
        this._offset = new Phaser.Math.Vector2( 0, 0 );

        /**
         * Scale value
         * @type {Phaser.Math.Vector2}
         * @private
         */
        this._scale = new Phaser.Math.Vector2( 1, 1 );

        /**
         * Whether the camera is tracking towards a target position
         */
        this.tracking = false;

        /**
         * Target position of the current camera track
         * @type {Phaser.Math.Vector2}
         * @private
         */
        this._trackTarget = new Phaser.Math.Vector2( 0, 0 );

        /**
         * Speed of camera track movement, in pixels per millisecond.
         * This is more mathematically convenient than the public version,
         * which is in the human-readable pixels per second.
         * @private
         */
        this._trackSpeed = 0.1;

        // Prepopulate camera bounds.
        const m = Number.MAX_SAFE_INTEGER;
        this.setBounds( -m / 2, -m / 2, m, m );
    },

    /**
     * Camera bounds object. This is a direct reference.
     * Alter it with caution. It's intended for direct control of
     * camera minimum/maximum bounds via left/right/top/bottom properties.
     * @type {Phaser.Geom.Rectangle}
     * @readonly
     */
    bounds: {
        get: function () {
            return this._bounds;
        },
    },

    /**
     * Horizontal camera offset. This is applied AFTER postUpdate,
     * so it is useful for cameras that follow off-center objects.
     * @property {Number} offsetX
     */
    offsetX: {
        get: function() {
            return this._offset.x;
        },
        set: function( value ) {
            this._offset.x = value;
        },
    },

    /**
     * Vertical camera offset. This is applied AFTER postUpdate,
     * so it is useful for cameras that follow off-center objects.
     * @property {Number} offsetY
     */
    offsetY: {
        get: function() {
            return this._offset.y;
        },
        set: function( value ) {
            this._offset.y = value;
        },
    },

    /**
     * Horizontal scroll. Setting this will set the `scrollX` property
     * while respecting camera bounds. Use this by preference.
     * @type {Number}
     */
    posX: {
        get: function () {
            return this._scrollX;
        },
        set: function ( value ) {
            this._scrollX = this.clampX( value );
            this.dirty = true;
        },
    },

    /**
     * Vertical scroll. Setting this will set the `scrollY` property
     * while respecting camera bounds. Use this by preference.
     * @type {Number}
     */
    posY: {
        get: function () {
            return this._scrollY;
        },
        set: function ( value ) {
            this._scrollY = this.clampY( value );
            this.dirty = true;
        },
    },

    /**
     * Horizontal scale factor
     * @property {Number} scaleX
     */
    scaleX: {
        get: function() {
            return this._scale.x;
        },
        set: function( value ) {
            this._scale.x = value;
            this._zoom = Math.max( this._scale.x, this._scale.y );
            this.dirty = true;
        },
    },

    /**
     * Vertical scale factor
     * @property {Number} scaleY
     */
    scaleY: {
        get: function() {
            return this._scale.y;
        },
        set: function( value ) {
            this._scale.y = value;
            this._zoom = Math.max( this._scale.x, this._scale.y );
            this.dirty = true;
        },
    },

    /**
     * Velocity of camera tracking, measured in pixels per second
     * @property {Number} trackSpeed
     */
    trackSpeed: {
        get: function () {
            return this._trackSpeed * 1000;
        },
        set: function ( value ) {
            this._trackSpeed = value / 1000;
        },
    },

    /**
     * The Camera zoom value. Change this value to zoom in, or out of, a Scene.
     * 
     * A value of 0.5 would zoom the Camera out,
     * so you can now see twice as much of the Scene as before.
     * A value of 2 would zoom the Camera in,
     * so every pixel now takes up 2 pixels when rendered.
     * 
     * Set to 1 to return to the default zoom level.
     * 
     * Be careful to never set this value to zero.
     * 
     * In `CameraExtended`, this value may be overridden by `scaleX` / `scaleY`.
     * It is equal to the larger of the two values.
     * If you set it, it overrides the two in turn.
     * 
     * @property {number} zoom
     * @default 1
     */
    zoom: {
        get: function() {
            return this._zoom;
        },
        set: function( value ) {
            this._scale.x = value;
            this._scale.y = value;
            this._zoom = value;
            this.dirty = true;
        },
    },

    /**
     * Track the camera towards its target.
     * @param {Number} delta - frame delta
     */
    _calculateTrack: function( delta ) {
        const target = this._trackTarget;
        const posX = this.posX;
        const posY = this.posY;
        const targetX = target.x;
        const targetY = target.y;
        const dx = targetX - posX;
        const dy = targetY - posY;
        const angle = Math.atan2( dy, dx );
        const maxStep = delta * this._trackSpeed;
        const distance = Math.hypot( dx, dy );
        if ( distance <= maxStep ) {
            // Reach the target and stop tracking.
            this.posX = targetX;
            this.posY = targetY;
            this.tracking = false;
        } else {
            // Move towards the target.
            this.posX += maxStep * Math.cos( angle );
            this.posY += maxStep * Math.sin( angle );
        }
    },

    /**
     * Update the camera.
     * 
     * This should run at the very start of the `postUpdate`
     * step during the scene update cycle, before any scene children run.
     * 
     * @param {Number} time - game time
     * @param {Number} delta - frame delta
     */
    postUpdate: function( time, delta ) {
        if ( this.tracking ) {
            this._calculateTrack( delta );
        }
    },

    /**
     * Internal preRender step.
     * 
     * Copied and modified from `Phaser.Cameras.Scene2D.Camera#preRender`
     * in Phaser v3.51.0.
     * All modified lines are tagged MODIFIED.
     */
    preRender: function ()
    {
        var width = this.width;
        var height = this.height;

        var halfWidth = width * 0.5;
        var halfHeight = height * 0.5;

        var scaleX = this._scale.x;   // MODIFIED from `zoom`
        var scaleY = this._scale.y;   // MODIFIED from `zoom`
        var matrix = this.matrix;

        var originX = width * this.originX;
        var originY = height * this.originY;

        var follow = this._follow;
        var deadzone = this.deadzone;

        var sx = this.scrollX;
        var sy = this.scrollY;

        if (deadzone)
        {
            CenterOn(deadzone, this.midPoint.x, this.midPoint.y);
        }

        var emitFollowEvent = false;

        if (follow && !this.panEffect.isRunning)
        {
            var fx = (follow.x - this.followOffset.x);
            var fy = (follow.y - this.followOffset.y);

            if (deadzone)
            {
                if (fx < deadzone.x)
                {
                    sx = Linear(sx, sx - (deadzone.x - fx), this.lerp.x);
                }
                else if (fx > deadzone.right)
                {
                    sx = Linear(sx, sx + (fx - deadzone.right), this.lerp.x);
                }

                if (fy < deadzone.y)
                {
                    sy = Linear(sy, sy - (deadzone.y - fy), this.lerp.y);
                }
                else if (fy > deadzone.bottom)
                {
                    sy = Linear(sy, sy + (fy - deadzone.bottom), this.lerp.y);
                }
            }
            else
            {
                sx = Linear(sx, fx - originX, this.lerp.x);
                sy = Linear(sy, fy - originY, this.lerp.y);
            }

            emitFollowEvent = true;
        }

        if (this.useBounds)
        {
            sx = this.clampX(sx);
            sy = this.clampY(sy);
        }

        if (this.roundPixels)
        {
            originX = Math.round(originX);
            originY = Math.round(originY);

            sx = Math.round(sx);
            sy = Math.round(sy);
        }

        //  Values are in pixels and not impacted by zooming the Camera
        this.scrollX = sx;
        this.scrollY = sy;

        var midX = sx + halfWidth;
        var midY = sy + halfHeight;

        //  The center of the camera, in world space, so taking zoom into account
        //  Basically the pixel value of what it's looking at in the middle of the cam
        this.midPoint.set(midX, midY);

        var displayWidth = width / scaleX;   // MODIFIED from `zoom`
        var displayHeight = height / scaleY;   // MODIFIED from `zoom`

        var vwx = midX - (displayWidth / 2);
        var vwy = midY - (displayHeight / 2);

        if (this.roundPixels)
        {
            vwx = Math.round(vwx);
            vwy = Math.round(vwy);
        }

        this.worldView.setTo(vwx, vwy, displayWidth, displayHeight);

        matrix.applyITRS(this.x + originX, this.y + originY, this.rotation, scaleX, scaleY );   // MODIFIED from `zoom`
        matrix.translate(               // MODIFIED
            -originX - this._offset.x,    // MODIFIED
            -originY - this._offset.y );  // MODIFIED

        this.shakeEffect.preRender();

        if (emitFollowEvent) {
            this.emit(Events.FOLLOW_UPDATE, this, follow);
        }
    },

    /**
     * Track the camera to the target location at the defined speed.
     * This will cause the camera to stop following anything.
     * The camera will automatically begin tracking during the
     * `postUpdate` phase.
     * 
     * This is very similar to `pan()`, but instead of moving with time,
     * it moves with space. We use "track" in the traditional
     * cinematographical sense, translating a camera, as "pan" was taken
     * (and technically means _rotating_ the camera).
     * 
     * @param {Number} x - horizontal target coordinate
     * @param {Number} y - vertical target coordinate
     */
    trackTo: function ( x, y ) {
        this.tracking = true;
        this._trackTarget.setTo( x, y );

        this.stopFollow();
    },
} );

export default CameraExtended;
